import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { Box, ButtonGroup, Center, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { CockpitPanelIntlKeysEnum } from '@components/cockpit/cockpit.types';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import CockpitPanelHeader from '@components/cockpit/panels/CockpitPanelHeader';
import Panel from '@components/cockpit/panels/Panel';
import { DeleteZoneModal } from '@components/cockpit/panels/zoneManagement/DeleteZoneModal';
import ZoneFormContainer from '@components/cockpit/panels/zoneManagement/form/ZoneFormContainer';
import { ZoneCodeAndType } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import {
  useZoneManagementContext,
  ZoneManagementContextProvider,
} from '@components/cockpit/panels/zoneManagement/ZoneManagementContext';
import { ZoneManagementFilters } from '@components/cockpit/panels/zoneManagement/ZoneManagementFilters';
import { DEFAULT_ZONE_FILTERS } from '@components/cockpit/panels/zoneManagement/ZoneManagementFilters.types';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasAnyRoles, hasRole } from '@redux/authent/authent.selectors';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { RoleEnum } from '@/types/authent/roles.types';
import { NoFlyZone, NoFlyZone3d, Zone, zoneCategoryToTypeFormMapper, ZoneTypeEnum } from '@/types/config/config.types';

const CREATE_ZONE_ROLES = [RoleEnum.CREATE_ZONE_IMZ, RoleEnum.CREATE_ZONE_NFZ, RoleEnum.CREATE_ZONE_NIZ];
//const EDIT_ZONE_ROLES = [RoleEnum.EDIT_ZONE_IMZ, RoleEnum.EDIT_ZONE_NFZ, RoleEnum.EDIT_ZONE_NIZ];
//const DELETE_ZONE_ROLES = [RoleEnum.DELETE_ZONE_IMZ, RoleEnum.DELETE_ZONE_NFZ, RoleEnum.DELETE_ZONE_NIZ];
interface ZonesActionsProps {
  zonesLength: number;
  onCreate: () => void;
}

function ZonesActions({ zonesLength, onCreate }: Readonly<ZonesActionsProps>) {
  const canCreateZone = useAppSelector((state) => hasAnyRoles(state, CREATE_ZONE_ROLES));

  return (
    <>
      <Box height="56px" alignContent="center">
        <Text color="neutral.500" textTransform="lowercase" userSelect="none">
          <FormattedMessage id="components.zones.result" values={{ count: zonesLength }} />
        </Text>
      </Box>
      {canCreateZone && (
        <IconButton aria-label="Add zone" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function ZonesHeader() {
  return (
    <>
      <Text width="260px" fontWeight="medium" color="neutral.500">
        <FormattedMessage id="components.zones.name" />
      </Text>
      <Text width="200px" fontWeight="medium" color="neutral.500">
        <FormattedMessage id="components.zones.groundProjection" />
      </Text>
      <Text width="240px" fontWeight="medium" color="neutral.500">
        <FormattedMessage id="components.zones.type.title" />
      </Text>
      <Text width="128px" fontWeight="medium" color="neutral.500">
        <FormattedMessage id="components.zones.activeStatus" />
      </Text>
    </>
  );
}

interface EmptyZoneListFallbackProps {
  setEditedZoneCodeAndType: Dispatch<SetStateAction<ZoneCodeAndType | null | undefined>>;
}

function EmptyZoneListFallback({ setEditedZoneCodeAndType }: Readonly<EmptyZoneListFallbackProps>) {
  const { filters } = useZoneManagementContext();
  const canCreateZone = useAppSelector((state) => hasAnyRoles(state, CREATE_ZONE_ROLES));

  const filtersOn = !shallowEqual(filters, DEFAULT_ZONE_FILTERS);

  const creationFallback = canCreateZone ? (
    <VStack
      padding={4}
      gap={2}
      marginY="auto"
      className="group"
      border="2px solid"
      cursor="pointer"
      borderColor="neutral.600"
      _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
      onClick={() => setEditedZoneCodeAndType(undefined)}
    >
      <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
      <Text>
        <FormattedMessage id="components.zones.createFirst" />
      </Text>
    </VStack>
  ) : undefined;

  return filtersOn ? (
    <Center height="100%" width="100%" backgroundColor="neutral.black">
      <VStack gap={2} paddingTop="58px">
        <Icon boxSize="194px" as={EmptyPanelDroneIcon} />
        <Text color="neutral.200" textAlign="center" userSelect="none">
          <FormattedMessage id="components.zones.filters.noResult" />
        </Text>
      </VStack>
    </Center>
  ) : (
    creationFallback
  );
}

interface ZonesProps {
  setEditedZoneCodeAndType: Dispatch<SetStateAction<ZoneCodeAndType | null | undefined>>;
}

function Zones({ setEditedZoneCodeAndType }: Readonly<ZonesProps>) {
  const { formatMessage } = useIntl();
  const { zones } = useZoneManagementContext();

  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const canEditImz = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_ZONE_IMZ));
  const canEditNfz = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_ZONE_NFZ));
  const canEditNiz = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_ZONE_NIZ));
  const canEditZone: Partial<Record<ZoneTypeEnum, boolean>> = {
    [ZoneTypeEnum.IMZ]: canEditImz,
    [ZoneTypeEnum.NFZ]: canEditNfz,
    [ZoneTypeEnum.NIZ]: canEditNiz,
  };

  const canDeleteImz = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_ZONE_IMZ));
  const canDeleteNfz = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_ZONE_NFZ));
  const canDeleteNiz = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_ZONE_NIZ));
  const canDeleteZone: Partial<Record<ZoneTypeEnum, boolean>> = {
    [ZoneTypeEnum.IMZ]: canDeleteImz,
    [ZoneTypeEnum.NFZ]: canDeleteNfz,
    [ZoneTypeEnum.NIZ]: canDeleteNiz,
  };

  return (
    <CockpitListLayout
      dialog={
        selectedZone !== null && <DeleteZoneModal selectedZone={selectedZone} setSelectedZone={setSelectedZone} />
      }
      actions={<ZonesActions zonesLength={zones.length} onCreate={() => setEditedZoneCodeAndType(undefined)} />}
      header={zones.length > 0 && <ZonesHeader />}
    >
      <For each={zones} fallback={<EmptyZoneListFallback setEditedZoneCodeAndType={setEditedZoneCodeAndType} />}>
        {(zone, index) => {
          const isNfz = [ZoneTypeEnum.NFZ, ZoneTypeEnum.NFZ_3D].includes(zone.type);
          const nfzSuffix = isNfz ? `.${(zone as NoFlyZone | NoFlyZone3d).level}` : '';
          const zoneTypeLabel = formatMessage({
            id: `components.zones.type.${zone.type}${nfzSuffix}`,
          });

          return (
            <CockpitRow index={index} listLength={zones.length} key={index}>
              <Tooltip content={zone.name} openDelay={500}>
                <Text width="260px" lineClamp={1} fontWeight="medium" color="neutral.200">
                  {zone.name}
                </Text>
              </Tooltip>
              <Text width="200px" lineClamp={1} fontWeight="medium" color="neutral.200">
                <FormattedMessage id="global.noInformation" />
              </Text>
              <Tooltip content={zoneTypeLabel} openDelay={500}>
                <Text width="240px" lineClamp={1} fontWeight="medium" color="neutral.200">
                  {zoneTypeLabel}
                </Text>
              </Tooltip>
              <Box width="128px">
                <Text
                  width="fit-content"
                  lineClamp={1}
                  fontWeight="medium"
                  color={zone.active ? 'green' : 'neutral.300'}
                  paddingX={0.5}
                  backgroundColor="neutral.900"
                >
                  <FormattedMessage id={`components.zones.${zone.active ? 'activated' : 'disabled'}`} />
                </Text>
              </Box>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditZone[zone.type] && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => {
                      const type = zoneCategoryToTypeFormMapper(zone.type, 'level' in zone ? zone.level : undefined);
                      if (type) {
                        setEditedZoneCodeAndType({
                          code: zone.code,
                          type,
                        });
                      }
                    }}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canDeleteZone[zone.type] && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedZone(zone)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}

export function ZoneManagement() {
  const { formatMessage } = useIntl();
  const [editedZoneCodeAndType, setEditedZoneCodeAndType] = useState<ZoneCodeAndType | null | undefined>(null);

  return (
    <ZoneManagementContextProvider>
      <Panel
        header={
          <CockpitPanelHeader label={formatMessage({ id: `components.sidebar.${CockpitPanelIntlKeysEnum.ZONE}` })} />
        }
      >
        {editedZoneCodeAndType === null ? (
          <HStack gap={0.25} width="100%" height="100%" backgroundColor="neutral.black">
            <ZoneManagementFilters />
            <Zones setEditedZoneCodeAndType={setEditedZoneCodeAndType} />
          </HStack>
        ) : (
          <ZoneFormContainer
            editedZoneCodeAndType={editedZoneCodeAndType}
            setEditedZoneCodeAndType={setEditedZoneCodeAndType}
          />
        )}
      </Panel>
    </ZoneManagementContextProvider>
  );
}
